import { BookmarkIcon, EyeIcon, NoSymbolIcon, StarIcon } from "@heroicons/react/24/solid"
import type React from "react"
import { forwardRef } from "react"
import type { Score } from "~/server/scores.server"
import { getScoreLabelText, selectedScoreStyle } from "~/utils/ratings"

const ACTIONS = {
	want: { Icon: BookmarkIcon, short: "Want", on: "Want to See", off: "Want to See", active: "bg-amber-500 text-black", tint: "text-amber-300" },
	seen: { Icon: EyeIcon, short: "Seen", on: "Seen", off: "Mark as Seen", active: "bg-green-500 text-black", tint: "text-green-300" },
	hide: { Icon: NoSymbolIcon, short: "Not interested", on: "Not interested", off: "Not interested", active: "bg-pink-500 text-black", tint: "text-pink-300" },
} as const

export type ActionKind = keyof typeof ACTIONS

const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"

/** Want to See on a title that is Seen: it goes on the Wishlist to be watched again. */
const REWATCH = { short: "Rewatch", long: "Want to rewatch" }

/**
 * Want to See, Seen, or Not interested as a toggle button.
 * `label`: "auto" shows the short word on phones and the long one from sm up; "long" always the long one; "short" always the short one; "none" the
 * icon alone, with the long label as its name and tooltip; "wide" the icon alone on phones and the long label from
 * sm up. `size`: "md" is 44px high, "sm" 36px.
 */
export function ActionButton({
	kind,
	active,
	label = "auto",
	size = "md",
	className = "",
	count,
	opensLog = false,
	rewatch = false,
	...rest
}: {
	kind: ActionKind
	/** Want to See only: the title is Seen, so the button reads "Want to rewatch". */
	rewatch?: boolean
	active: boolean
	label?: "auto" | "long" | "short" | "none" | "wide"
	size?: "md" | "sm"
	/** Seen with the movie watch log: "3×" from two watches on. */
	count?: string | null
	/** Seen with the movie watch log: the press opens the log and takes nothing back. */
	opensLog?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
	const a = ACTIONS[kind]
	const again = rewatch && kind === "want"
	const long = again ? REWATCH.long : active ? a.on : a.off
	const short = again ? REWATCH.short : active ? a.on : a.short
	const name = `${long}${count ? `, watched ${count}` : ""}${opensLog ? ". Open your watches" : ""}`
	const padding =
		label === "none"
			? "px-0"
			: label === "wide"
				? "px-0 text-xs sm:px-2"
				: label === "auto"
				? "px-2 text-xs sm:px-4 sm:text-sm"
				: size === "md"
					? "px-3 text-sm"
					: "px-2 text-xs"
	return (
		<button
			type="button"
			aria-pressed={active}
			aria-label={label === "none" || opensLog ? name : undefined}
			aria-haspopup={opensLog ? "dialog" : undefined}
			title={label === "none" ? name : undefined}
			className={`relative inline-flex ${size === "md" ? "h-11" : "h-9"} w-full min-w-0 items-center justify-center gap-2 rounded-lg ${padding} font-semibold cursor-pointer transition-colors ${FOCUS} disabled:cursor-not-allowed disabled:opacity-40 ${
				active ? a.active : "bg-white/10 text-gray-100 hover:bg-white/20"
			} ${className}`}
			{...rest}
		>
			<a.Icon className={`h-4 w-4 shrink-0 ${active ? "" : a.tint}`} />
			{/* Icon alone: the count sits in the corner, so a narrow button never cuts it off. */}
			{label === "none" && count && (
				<span className="absolute bottom-0.5 right-0.5 rounded bg-black/30 px-0.5 text-[9px] font-bold leading-tight tabular-nums">{count}</span>
			)}
			{label === "auto" && (
				<>
					<span className="truncate sm:hidden">{short}</span>
					<span className="hidden truncate sm:inline">{long}</span>
				</>
			)}
			{label === "long" && <span className="truncate">{long}</span>}
			{label === "short" && <span className="truncate">{short}</span>}
			{label === "wide" && <span className="truncate max-sm:sr-only">{long}</span>}
			{label !== "none" && count && <span className="shrink-0 tabular-nums opacity-80">{count}</span>}
			{/* Drawn here: the icon library's arrow would join the scripts of every page. */}
			{label !== "none" && opensLog && (
				<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true">
					<path d="m4 6 4 4 4-4" />
				</svg>
			)}
		</button>
	)
}

/** The score button in a card's row of quick actions: a star, or the person's score on its vibe color. */
export const ScoreButton = forwardRef<
	HTMLButtonElement,
	{ score: Score | null } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function ScoreButton({ score, className = "", ...rest }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			aria-label={score ? `Your score: ${getScoreLabelText(score)}. Change it` : "Score it"}
			title={score ? `Your score: ${getScoreLabelText(score)}` : "Score it"}
			className={`inline-flex h-9 w-full min-w-0 items-center justify-center rounded-lg px-0 text-sm font-bold cursor-pointer transition-colors ${FOCUS} ${
				score ? "text-white" : "bg-white/10 text-gray-100 hover:bg-white/20"
			} ${className}`}
			style={score ? selectedScoreStyle(score) : undefined}
			{...rest}
		>
			{score ? <span className="truncate px-1 text-xs tabular-nums">{getScoreLabelText(score)}</span> : <StarIcon className="h-4 w-4 shrink-0 text-yellow-300" />}
		</button>
	)
})
